import React, { useEffect, useState } from 'react'
import { ShoppingCart, User, Globe, Moon, Sun } from 'lucide-react'
import { useTheme } from '../../hooks/useTheme'
import { useTranslation } from '../../hooks/useTranslation'
import { getCart } from '../../services/cart'
import { me } from '../../services/auth'

const TopBar: React.FC = () => {
  const { theme, toggleTheme } = useTheme()
  const { t, currentLanguage, changeLanguage, isRTL } = useTranslation()

  const handleLanguageToggle = () => {
    const newLanguage = currentLanguage === 'ar' ? 'en' : 'ar'
    changeLanguage(newLanguage)
  }

  const [cartCount, setCartCount] = useState<number>(0)
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false)

  useEffect(() => {
    const apply = (c: any) => {
      const count = Array.isArray(c?.items) ? c.items.reduce((n: number, it: any) => n + (it.quantity || 0), 0) : 0
      setCartCount(count)
    }
    getCart().then(apply).catch(() => setCartCount(0))
    me().then(u => setIsLoggedIn(!!u)).catch(() => setIsLoggedIn(false))
    const onUpdated = (e: Event) => {
      // @ts-ignore
      apply(e.detail)
    }
    window.addEventListener('cart:updated', onUpdated)
    return () => window.removeEventListener('cart:updated', onUpdated)
  }, [])

  const handleHomeClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (window.location.pathname === '/') {
      e.preventDefault()
      document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div className="bg-surface border-b border-border text-text-2 py-2.5 transition-colors duration-200">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex justify-between items-center text-xs sm:text-sm flex-wrap gap-3">
          
          {/* الروابط الرئيسية */}
          <div className={`hidden sm:flex items-center space-x-5 ${isRTL ? 'rtl:space-x-reverse' : ''}`}>
            <a
              href="/#hero"
              onClick={handleHomeClick}
              className="transition-colors duration-150 hover:text-gold"
            >
              {t('navbar.topBar.mainLinks.home')}
            </a>
            <a href="/catalog" className="transition-colors duration-150 hover:text-gold">
              {t('navbar.topBar.mainLinks.catalog')}
            </a>
            <a href="/contact" className="transition-colors duration-150 hover:text-gold">
              {t('navbar.topBar.mainLinks.contact')}
            </a>
            <a href="/about" className="transition-colors duration-150 hover:text-gold">
              {t('navbar.topBar.mainLinks.about')}
            </a>
          </div>

          {/* منطقة الحساب والتحكم */}
          <div className={`flex items-center space-x-5 ${isRTL ? 'rtl:space-x-reverse' : ''}`}>
            
            {/* السلة */}
            <a
              href="/cart"
              className={`flex items-center space-x-2 transition-colors duration-150 hover:text-cyan ${isRTL ? 'rtl:space-x-reverse' : ''}`}
            >
              <ShoppingCart size={16} />
              <span className="hidden sm:inline">{t('navbar.topBar.auth.cart')}</span>
              <span className="bg-gold text-bg text-[11px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-sm">
                {cartCount}
              </span>
            </a>

            {/* المستحدم */}
            <a
              href={isLoggedIn ? '/dashboard' : '/register'}
              className={`flex items-center space-x-2 transition-colors duration-150 hover:text-gold ${isRTL ? 'rtl:space-x-reverse' : ''}`}
              title={isLoggedIn ? 'لوحة التحكم' : t('navbar.topBar.auth.signUp')}
            >
              <User size={16} />
            </a>

            {/* تبديل اللغة */}
            <button
              onClick={handleLanguageToggle}
              className={`flex items-center space-x-2 transition-colors duration-150 hover:text-cyan ${isRTL ? 'rtl:space-x-reverse' : ''}`}
              aria-label={currentLanguage === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
            >
              <Globe size={16} />
              <span className="sm:hidden">{currentLanguage === 'ar' ? 'العربية' : 'English'}</span>
              <span className="hidden sm:inline">{t(`navbar.topBar.language.${currentLanguage === 'ar' ? 'en' : 'ar'}`)}</span>
            </button>

            {/* زر تغيير الثيم */}
            <button
              onClick={toggleTheme}
              className="p-1 rounded-md text-text-2 hover:text-gold hover:bg-surface-soft transition-all duration-150"
              aria-label={theme === 'dark' ? t('navbar.topBar.theme.light') : t('navbar.topBar.theme.dark')}
            >
              {theme === 'dark' ? <Sun size={16} className="text-gold" /> : <Moon size={16} />}
            </button>
          </div>

        </div>
      </div>
    </div>
  )
}

export default TopBar